import { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import {
    Box,
    Drawer,
    AppBar,
    Toolbar,
    Typography,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Collapse,
} from '@mui/material';
import {
    Storage as SchemaIcon,
    CompareArrows as MatchIcon,
    Folder as ProjectIcon,
    ManageSearch as ReportLookupIcon,
    ExpandLess,
    ExpandMore,
    Settings as SettingsIcon,
    Rule as PreferencesIcon,
} from '@mui/icons-material';

const DRAWER_WIDTH = 260;

/**
 * Layout Component with MetLife-styled Sidebar
 */
function Layout() {
    const location = useLocation();
    const [schemaOpen, setSchemaOpen] = useState(true);
    const [reportOpen, setReportOpen] = useState(true);

    const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            {/* Sidebar */}
            <Drawer
                variant="permanent"
                sx={{
                    width: DRAWER_WIDTH,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: DRAWER_WIDTH,
                        boxSizing: 'border-box',
                        borderRight: 'none',
                    },
                }}
            >
                {/* Logo Area */}
                <Box
                    sx={{
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: 0.35,
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                    }}
                >
                    <Box
                        component="svg"
                        viewBox="0 0 260 64"
                        aria-label="MetLife logo"
                        role="img"
                        sx={{
                            width: 165,
                            height: 'auto',
                            display: 'block',
                            flexShrink: 0,
                            overflow: 'visible',
                            marginLeft: '-2px',
                        }}
                    >
                        <defs>
                            <linearGradient id="metlife-mark-left" x1="0%" y1="0%" x2="0%" y2="100%">
                                <stop offset="0%" stopColor="#8ED8F5" />
                                <stop offset="100%" stopColor="#56B9E9" />
                            </linearGradient>
                            <linearGradient id="metlife-mark-right" x1="100%" y1="0%" x2="0%" y2="100%">
                                <stop offset="0%" stopColor="#C8E86C" />
                                <stop offset="100%" stopColor="#6BD7B0" />
                            </linearGradient>
                        </defs>
                        <g transform="translate(0 6)">
                            <path d="M8 0C20 0 31 5 39 14V50H8V0Z" fill="url(#metlife-mark-left)" />
                            <path d="M40 14C48 5 59 0 72 0V50H40V14Z" fill="url(#metlife-mark-right)" />
                            <path d="M40 14V50H8V0C20 0 31 5 39 14Z" fill="url(#metlife-mark-left)" opacity="0.92" />
                        </g>
                        <text
                            x="88"
                            y="44"
                            fill="#FFFFFF"
                            fontFamily="Arial, Helvetica, sans-serif"
                            fontSize="44"
                            fontWeight="700"
                            letterSpacing="-1.2"
                        >
                            MetLife
                        </text>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', minWidth: 0, pl: '55px' }}>
                        <Typography
                            variant="caption"
                            sx={{
                                color: '#D7E7F5',
                                whiteSpace: 'nowrap',
                                lineHeight: 1,
                                fontSize: '0.88rem',
                                fontWeight: 600,
                                textAlign: 'left',
                                letterSpacing: '0.02em',
                            }}
                        >
                            AI Reports Accelerator
                        </Typography>
                    </Box>
                </Box>

                {/* Navigation */}
                <Box sx={{ p: 1, mt: 1, flex: 1, overflowY: 'auto' }}>
                    <List component="nav" disablePadding>
                        {/* Schema Analyzer */}
                        <ListItem disablePadding>
                            <ListItemButton
                                onClick={() => setSchemaOpen(!schemaOpen)}
                                selected={isActive('/schema-analyzer')}
                            >
                                <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                                    <SchemaIcon />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Schema Analyzer"
                                    primaryTypographyProps={{ fontWeight: 500 }}
                                />
                                {schemaOpen ? <ExpandLess /> : <ExpandMore />}
                            </ListItemButton>
                        </ListItem>

                        <Collapse in={schemaOpen} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                                <ListItemButton
                                    component={Link}
                                    to="/schema-analyzer/projects"
                                    selected={isActive('/schema-analyzer/projects')}
                                    sx={{ pl: 4 }}
                                >
                                    <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                                        <ProjectIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Projects"
                                        primaryTypographyProps={{ fontSize: '0.9rem' }}
                                    />
                                </ListItemButton>
                                <ListItemButton
                                    component={Link}
                                    to="/schema-analyzer/preferences"
                                    selected={isActive('/schema-analyzer/preferences')}
                                    sx={{ pl: 4 }}
                                >
                                    <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                                        <PreferencesIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Preferences"
                                        primaryTypographyProps={{ fontSize: '0.9rem' }}
                                    />
                                </ListItemButton>
                            </List>
                        </Collapse>

                        {/* Report Matching */}
                        <ListItem disablePadding sx={{ mt: 1 }}>
                            <ListItemButton
                                onClick={() => setReportOpen(!reportOpen)}
                                selected={isActive('/report-matching')}
                            >
                                <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                                    <MatchIcon />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Report Matching"
                                    primaryTypographyProps={{ fontWeight: 500 }}
                                />
                                {reportOpen ? <ExpandLess /> : <ExpandMore />}
                            </ListItemButton>
                        </ListItem>

                        <Collapse in={reportOpen} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding>
                                <ListItemButton
                                    component={Link}
                                    to="/report-matching/workspaces"
                                    selected={isActive('/report-matching/workspaces')}
                                    sx={{ pl: 4 }}
                                >
                                    <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                                        <ProjectIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Workspaces"
                                        primaryTypographyProps={{ fontSize: '0.9rem' }}
                                    />
                                </ListItemButton>
                                <ListItemButton
                                    component={Link}
                                    to="/report-matching/report-lookup"
                                    selected={isActive('/report-matching/report-lookup')}
                                    sx={{ pl: 4 }}
                                >
                                    <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                                        <ReportLookupIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Report Lookup"
                                        primaryTypographyProps={{ fontSize: '0.9rem' }}
                                    />
                                </ListItemButton>
                                <ListItemButton
                                    component={Link}
                                    to="/report-matching/preferences"
                                    selected={isActive('/report-matching/preferences')}
                                    sx={{ pl: 4 }}
                                >
                                    <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                                        <PreferencesIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Preferences"
                                        primaryTypographyProps={{ fontSize: '0.9rem' }}
                                    />
                                </ListItemButton>
                            </List>
                        </Collapse>
                    </List>
                </Box>

                {/* Configurations - Bottom of sidebar */}
                <Box sx={{ p: 1, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                    <List component="nav" disablePadding>
                        <ListItem disablePadding>
                            <ListItemButton
                                component={Link}
                                to="/keyword-config"
                                selected={isActive('/keyword-config')}
                            >
                                <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                                    <SettingsIcon />
                                </ListItemIcon>
                                <ListItemText
                                    primary="Configurations"
                                    primaryTypographyProps={{ fontWeight: 500, fontSize: '0.9rem' }}
                                />
                            </ListItemButton>
                        </ListItem>
                    </List>
                </Box>

                {/* Footer */}
                <Box sx={{ p: 2, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                    <Typography variant="caption" sx={{ opacity: 0.5 }}>
                        © 2026 MetLife
                    </Typography>
                </Box>
            </Drawer>

            {/* Main Content */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    bgcolor: 'background.default',
                    minHeight: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {/* Top Bar */}
                <AppBar
                    position="static"
                    elevation={0}
                    sx={{
                        bgcolor: 'background.paper',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <Toolbar>
                        <Typography variant="h6" color="text.primary" fontWeight={600}>
                            {location.pathname.includes('schema-analyzer')
                                ? 'Schema Analyzer'
                                : location.pathname.includes('report-matching')
                                    ? 'Report Matching'
                                    : location.pathname.includes('keyword-config')
                                        ? 'Configurations'
                                        : 'Dashboard'}
                        </Typography>
                    </Toolbar>
                </AppBar>

                {/* Page Content */}
                <Box sx={{ p: 3, flex: 1 }}>
                    <Outlet />
                </Box>
            </Box>
        </Box>
    );
}

export default Layout;
